import { Component, ElementRef, Input, Renderer2 } from '@angular/core';
import { System, isNotNullOrUndefined } from 'ngx-core-services';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, first, mapTo, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, combineLatest, defer } from 'rxjs';

import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { requestPlantCardBlocksToShow, requestPlantCardStatus, selectSystemMetadata, SystemsState } from '../../store';
import { selectPlantCardAlertStateBySystemId, selectPlantCardBlocksToShowBySystemId, selectPlantCardStateBySystemId } from '../../store/selectors/plant-card.selectors';

const getLabels = () => ({
  connectedAndUpdated: 'Connected and updated',
  disconnected: 'Disconnected',
  notUpdated: 'Not updated',
  updated: 'Updated',
  hasAlerts: 'Has Alerts',
});

@CreateBlockReduxSupport({
  requestAddSystemWithSelection: false,
  requestAddSystemWithSubscription: false,
})
@Component({
  selector: 'lib-plant-card-overview',
  templateUrl: './plant-card-overview.component.html',
  styleUrls: ['./plant-card-overview.component.scss'],
})
export class PlantCardOverviewComponent implements IReduxBlock {
  @Input() headerType: 'plantName' | 'plantStatus' = 'plantName';

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  @Input() notDevices = false;
  @Input() notStatus = false;

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  readonly _instanceId$: Observable<string> = new Observable<string>();
  readonly blocksToShow$ = this.system$.pipe(
    isNotNullOrUndefined(),
    switchMap(({ uid }) => this.store.select(selectPlantCardBlocksToShowBySystemId(uid!))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly plantStatus$ = this.system$.pipe(
    isNotNullOrUndefined(),
    switchMap(({ uid }) => this.store.select(selectPlantCardStateBySystemId(uid!))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly plantHasAlerts$ = this.system$.pipe(
    isNotNullOrUndefined(),
    switchMap(({ uid }) => this.store.select(selectPlantCardAlertStateBySystemId(uid!))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  get plantName() {
    return this.system$.value?.name || '';
  }

  readonly telemetryStatus$ = combineLatest([
    this.system$.pipe(
      isNotNullOrUndefined(),
    ),
    defer(() => this.refresh$).pipe(
      mapTo(true),
      startWith(false),
    ),
    this.system$.pipe(
      switchMap((system) => this.store.select(selectSystemMetadata(system?.uid!))),
      distinctUntilChanged(),
    ),
  ]).pipe(
    switchMap(async ([system, forceRefresh, metadata]) => {
      const blockToShow = await this.store.select(selectPlantCardBlocksToShowBySystemId(system.uid!))
        .pipe(first())
        .toPromise();

      const plantStatus = await this.store.select(selectPlantCardStateBySystemId(system.uid!))
        .pipe(first())
        .toPromise();

      if (metadata || forceRefresh || !blockToShow || !plantStatus) {
        this.store.dispatch(requestPlantCardBlocksToShow({ systemId: system.uid!, forceRefresh }));
        this.store.dispatch(requestPlantCardStatus({ systemId: system.uid!, forceRefresh }));
      }

      return { system };
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly deviceCountSkeleton = Array(Math.floor(Math.random() * 2) + 2).fill(0);

  constructor(
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    readonly store: Store<SystemsState>,
  ) { }
}
